import { Fragment, lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { api, ApiError } from './lib/api'
import type { Identity } from './lib/types'
import { afterSessionEnded, noticeText, refusalOf, type Notice, type SessionEndedDetail } from './lib/sessionEnded'
import { afterSignOut } from './lib/directory'
import { languageToKeep, rememberedLanguage, rememberLanguage, signInLanguage } from './lib/staffLanguage'
import { ENGLISH_ONLY, isStaffLanguage, t, type StaffLanguage } from './lib/staffText'
import { hasUnsaved } from './lib/unsaved'
import { checkBuild, staleBuild, watchBuild } from './lib/newBuild'
import { SignIn } from './pages/SignIn'
import { ChangePassword } from './pages/ChangePassword'
import { Account } from './pages/Account'
import { People } from './pages/People'
import { Administration } from './pages/Administration'
import { Reports } from './reports/pivot/ReportsPage'
import { Layouts } from './pages/Layouts'
import { Signage } from './pages/Signage'
import { Audit } from './pages/Audit'
import { Reception } from './pages/Reception'
import { Counter } from './pages/Counter'
import { Home, tilesFor } from './pages/Home'
import { nextLanding, pageAllowed } from './lib/homeTiles'
import { bookingsAccess, bookingsHeld, bookingsShutLine } from './lib/bookings'
import { EmptyState } from './ui/parts'
import { Icon } from './ui/icons'
import { Floor } from './pages/Floor'
import { Appointments } from './pages/Appointments'
import { StaffLanguageProvider } from './ui/StaffText'
import { setWording } from './lib/i18n/catalog'
import { staffLanguageOf } from './lib/i18n/staffLanguage'
import { SessionProvider } from './ui/Session'
import { Chrome } from './ui/Chrome'
import { StaffToasts } from './ui/toast'
import { ChooseOrganisation, useOrgContext } from './ui/OrgSwitch'
import { contextOrganisation, pageNeedsOrganisation, setContextOrganisation } from './lib/ownerOrg'
import type { StaffKey } from './lib/staffText'
import { setClientErrorsSignedIn } from './lib/clientErrors'

// The customer-facing pages come in their own chunk, carrying the Qmeter CSS
// they are drawn in (see pages/customerSurfaces).
const surfaces = () => import('./pages/customerSurfaces')
const Kiosk = lazy(() => surfaces().then((m) => ({ default: m.Kiosk })))
const Display = lazy(() => surfaces().then((m) => ({ default: m.Display })))
const Feedback = lazy(() => surfaces().then((m) => ({ default: m.Feedback })))
const Follow = lazy(() => surfaces().then((m) => ({ default: m.Follow })))

/** The heading of an org-scoped page, shown while the owner chooses an organisation. */
const PAGE_TITLES: Record<string, StaffKey> = {
  counter: 'entry.app.counter',
  reception: 'entry.app.reception',
  floor: 'entry.app.floor',
  appointments: 'entry.app.appointments',
  people: 'entry.app.people',
  screens: 'entry.app.designs',
  signage: 'entry.app.signage',
  reports: 'entry.app.reports',
  audit: 'entry.tile.audit',
}

/** A machine's address: a kiosk, a hall board or a feedback tablet. */
export const ADDRESSED = /^\/(kiosk|display|board|feedback)\/([A-Za-z0-9_-]{1,16})(?:\/([A-Za-z0-9_-]{1,16}))?$/

/** Whether a move must be a full page load: entering a machine's address keeps the staff CSS out,
 *  and leaving one sheds the screen worker that a pushState would leave running. */
export function needsFullLoad(from: string, to: string): boolean {
  return ADDRESSED.test(to) || to === '/t' || ADDRESSED.test(from)
}

/** Whether a page asks GET /auth/me: never the follow page, and a machine's address
 *  only in a browser somebody signed in on (a lobby screen never has, and would log a 401). */
export function asksWhoAmI(path: string, signedInHere: boolean): boolean {
  if (path === '/t') return false
  return !ADDRESSED.test(path) || signedInHere
}

const SIGNED_IN_HERE = 'qms.signedInHere'

function signedInHere(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN_HERE) === '1'
  } catch {
    return false
  }
}

function rememberSignedIn(yes: boolean) {
  try {
    if (yes) window.localStorage.setItem(SIGNED_IN_HERE, '1')
    else window.localStorage.removeItem(SIGNED_IN_HERE)
  } catch {
    // A convenience: without it a staff member's screen preview shows no name.
  }
}

// Routing.
//
// Three applications share one bundle and one session, because they share a branch:
// a supervisor moves between the reception desk and the floor view during a shift,
// and signing in again at each would be a tax paid all day. The paths are real
// paths rather than fragments, so a display can be pinned to a URL and a browser
// reload lands where it was.

export function App() {
  const [path, setPath] = useState(window.location.pathname)
  const [identity, setIdentity] = useState<Identity | null>(null)
  const org = useOrgContext(identity)
  // Uncaught errors are reported only while somebody is signed in (lib/clientErrors.ts).
  useEffect(() => setClientErrorsSignedIn(identity !== null), [identity])
  const [checking, setChecking] = useState(true)
  // Whether this load asked who is signed in; only then does its answer say so.
  const asked = useRef(false)
  useEffect(() => {
    if (!checking && asked.current) rememberSignedIn(identity !== null)
  }, [identity, checking])
  // Kept as a source (a phrase key, or the server's own refusal), not a resolved
  // sentence: the language menu sits right above wherever this is shown, and
  // a string baked in at set-time would be left in stale language the moment
  // that row is used. noticeText resolves it at render, in the current lang.
  const [notice, setNotice] = useState<Notice>(null)
  // Read before anybody has signed in: what this browser last showed, else the
  // organisation's own on a server with exactly one, else the browser's language.
  // The organisation list is not known yet at this point, so SignIn's own effect
  // narrows this further once it has fetched it.
  const [signInLang, setSignInLang] = useState<StaffLanguage>(() =>
    signInLanguage(rememberedLanguage(), [], typeof navigator === 'undefined' ? [] : (navigator.languages ?? [])),
  )

  // The reader's language: the signed-in person's own once there is one — which
  // is already their choice, else their organisation's, else English, worked out
  // by the server — and the pre-sign-in guess otherwise.
  const lang: StaffLanguage = staffLanguageOf(identity, signInLang, ENGLISH_ONLY)
  const langRef = useRef(lang)
  langRef.current = lang

  // The organisation's own words reach every phrase lookup (i18n/catalog.ts).
  useEffect(() => setWording(identity?.wording), [identity])

  // The document's language follows, for screen readers, spelling and translation prompts.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  // A language pressed on the sign-in screen on this visit, kept for the moment
  // of sign-in (see languageToKeep). A ref, not state: nothing renders from it.
  const pressedLang = useRef<StaffLanguage | null>(null)
  const onSignInLanguage = useCallback((next: StaffLanguage, chosen?: boolean) => {
    setSignInLang(next)
    if (chosen) pressedLang.current = next
  }, [])

  // Remembered so the sign-in screen — this person's next time, or the next
  // person's at this same desk — opens already in the language just used. Set
  // as the sign-in screen's own too, because that was worked out once when the
  // page loaded: without it, signing out or a session running out would show
  // the sign-in screen, and the notice saying why, in that older language.
  useEffect(() => {
    if (identity && isStaffLanguage(identity.language)) {
      rememberLanguage(identity.language)
      setSignInLang(identity.language)
    }
  }, [identity])

  useEffect(() => {
    // After a deploy, Back and Forward load the page fresh, as a move inside the app does.
    const onPop = () => (staleBuild() ? window.location.reload() : setPath(window.location.pathname))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // A session can end while a screen is open: the spell of inactivity runs out, or
  // an administrator disables the account. The client says so once, centrally,
  // instead of each screen quietly failing to refresh.
  //
  // "Who am I" is asked once first (lib/sessionEnded.ts): behind a proxy that
  // signs people in, that GET is the new sign-in. Several screens failing at once
  // raise several events, and they share the one question.
  const asking = useRef<Promise<void> | null>(null)
  useEffect(() => {
    const onSignedOut = (event: Event) => {
      const detail = (event as CustomEvent<SessionEndedDetail>).detail
      if (asking.current) return
      asking.current = afterSessionEnded(detail, () => api.me(), (err) => err instanceof ApiError && err.isUnauthenticated)
        .then((next) => {
          if (next.error) console.error(next.error)
          setIdentity(next.identity)
          setNotice(next.notice)
        })
        .finally(() => {
          asking.current = null
        })
    }
    window.addEventListener('qms:signed-out', onSignedOut)
    return () => window.removeEventListener('qms:signed-out', onSignedOut)
  }, [])

  const refresh = useCallback(
    () =>
      api
        .me()
        .then(setIdentity)
        .catch((err) => {
          if (!(err instanceof ApiError && err.isUnauthenticated)) console.error(err)
          setIdentity(null)
          // Behind a proxy this was the sign-in, and a refusal says why. Kept
          // unresolved (see the notice state above) so it reads correctly in
          // whichever language the sign-in screen ends up showing.
          const refusal = refusalOf(err)
          if (refusal) setNotice(refusal)
        }),
    [],
  )

  // After a settings save: a failed read keeps the session as it is.
  const reread = useCallback(
    () =>
      api
        .me()
        .then(setIdentity)
        .catch(() => undefined),
    [],
  )

  // The owner's modules and options are the chosen organisation's (/auth/me?organisation=).
  const ownerOrg = identity?.owner ? org.organisation : null
  const readFor = useRef<string | null>(null)
  useEffect(() => {
    const was = readFor.current
    readFor.current = ownerOrg
    if (ownerOrg !== null && was !== null && was !== ownerOrg) void reread()
  }, [ownerOrg, reread])

  useEffect(() => {
    // The follow page has no session and asks for none. Asking /auth/me there
    // would be a request a proxy that forwards only the follow routes answers
    // with an error, for nothing.
    // A lobby screen has no session either: asking would only log a 401.
    if (!asksWhoAmI(window.location.pathname, signedInHere())) {
      setChecking(false)
      return
    }
    asked.current = true
    // One question now: who is signed in. There used to be a second — whether this
    // installation had been set up at all — answered by a screen that minted an
    // administrator for whoever reached it first. A server ships with its owner
    // account instead, so a fresh installation is signed in to like any other.
    refresh().finally(() => setChecking(false))
  }, [refresh])

  // A staff page knows when a newer build is deployed; the next move inside the app loads it.
  useEffect(() => (ADDRESSED.test(window.location.pathname) ? undefined : watchBuild()), [])

  const navigate = useCallback((to: string) => {
    if (staleBuild() || needsFullLoad(window.location.pathname, to)) {
      window.location.assign(to)
      return
    }
    void checkBuild()
    window.history.pushState({}, '', to)
    // The page is chosen by the path alone; a hash says where on it (the Designs
    // page opens /screens#<design> at that design) and is announced, because
    // pushState does not, to a page that is already showing.
    setPath(to.replace(/[?#].*$/, ''))
    if (to.includes('#')) window.dispatchEvent(new HashChangeEvent('hashchange'))
  }, [])

  // A person with one app lands in it on their first screen after sign-in; the Q mark still opens Home.
  const landed = useRef(false)
  useLayoutEffect(() => {
    const next = nextLanding(landed.current, identity, window.location.pathname, () => (identity ? tilesFor(identity) : []))
    landed.current = next.landed
    if (next.to) {
      window.history.replaceState({}, '', next.to)
      setPath(next.to)
    }
  }, [identity])

  // The top bar's way out asks before it drops a page's unsaved edits.
  const leave = useCallback(
    (to: string) => {
      if (hasUnsaved() && !window.confirm(t('ui.discardAsk', langRef.current))) return
      navigate(to)
    },
    [navigate],
  )

  const signOut = useCallback(async () => {
    const elsewhere = afterSignOut(identity)
    await api.logout().catch(() => undefined)
    // Signed in by the organisation's own sign-in service: signing out here alone
    // would be undone by the next request, so the browser goes there instead.
    if (elsewhere) {
      window.location.assign(elsewhere)
      return
    }
    setIdentity(null)
    setNotice(null)
    setContextOrganisation('')
    navigate('/')
  }, [identity, navigate])

  // The language menu in the top bar, the one place a language is chosen. Signed
  // in, it is saved to the person (PATCH /auth/me works past the password wall
  // too) and the answer becomes the identity, so every screen switches at once.
  // Before sign-in it is this browser's choice, remembered, and kept for the
  // moment of sign-in (see languageToKeep). A failed save leaves the screens as
  // they were, which is itself the answer that nothing changed.
  const chooseLanguage = useCallback(
    async (next: StaffLanguage) => {
      if (!identity) {
        rememberLanguage(next)
        onSignInLanguage(next, true)
        return
      }
      try {
        setIdentity(await api.setMyLanguage(next))
      } catch (err) {
        console.error(err)
      }
    },
    [identity, onSignInLanguage],
  )

  // A person with no language of their own who pressed one on the sign-in
  // screen has it saved as theirs before the first screen shows, so that screen
  // (often the forced new-password one) and every one after it read in the
  // language they asked for, not their organisation's. Best effort: if saving
  // fails they read their organisation's, as before, and can choose again.
  const signedIn = useCallback((next: Identity) => {
    setNotice(null)
    const keep = languageToKeep(pressedLang.current, next)
    pressedLang.current = null
    if (!keep) {
      setIdentity(next)
      // An owner signing in again in a tab that kept an organisation reads its modules.
      if (next.owner && contextOrganisation()) void reread()
      return
    }
    api.setMyLanguage(keep).then(setIdentity, (err: unknown) => {
      console.error(err)
      setIdentity(next)
    })
  }, [reread])

  // A customer's phone, opened from the code on their slip. Before the sign-in
  // wall and before the session check, because it has neither: the ticket is named
  // by the token in the link's fragment — see pages/Follow.
  if (path === '/t')
    return (
      <Suspense fallback={null}>
        <Follow />
      </Suspense>
    )

  if (checking) {
    // Deliberately blank rather than a spinner: this resolves in a few
    // milliseconds against a local server, and a spinner that flashes is noise.
    return null
  }

  // Built as one value, screen, rather than returned straight from each branch:
  // every staff screen shares one language, chosen once, so the whole tree below
  // is wrapped in a single provider rather than each branch wrapping its own.
  const screen = (() => {
    // An unattended screen is addressed by URL: /display/BAKU-01/TV1 names the branch
    // and the machine. That is how a screen is provisioned by pushing an address to
    // it — the one thing a device manager can do remotely — and how an engineer
    // standing at a broken kiosk can read its identity off the address bar.
    //
    // Answered before the sign-in wall, and that is the point of this whole path. A
    // credential stored in a browser on a machine in a public lobby is a credential
    // anybody can walk up to; a television is not a thing that logs in; and a device
    // session that expires after thirty days means somebody driving to a branch to
    // type a password into a wall panel, forever.
    //
    // What makes it safe is not here. It is that the endpoints behind these two
    // pages answer with a redacted view — the numbers, the desks and the depth of
    // each queue, and never who is behind the counter or how the branch is
    // performing. See internal/api/screen.go.
    //
    // This is now the only way to be a screen. A machine used to be able to sign in
    // with a password instead and land on an unaddressed /kiosk or /display that took
    // its branch from the account; that meant one machine had two identities, and
    // which one it was using depended on how somebody had set it up.
    //
    // /display/... is the address, and /board/... answers too — it existed for a
    // few hours on 17 September 2026 and may be in somebody's notes. An address is
    // written into the autostart of a machine standing in a building, so this
    // product adds addresses and does not retire them.
    const addressed = ADDRESSED.exec(path)
    if (addressed) {
      const [, app, branchCode, unitCode] = addressed
      let page
      if (app === 'kiosk') {
        page = <Kiosk branchCode={branchCode} unitCode={unitCode} />
      } else if (app === 'feedback' && branchCode && unitCode) {
        // A feedback tablet at a desk, facing the customer. Addressed like the
        // others and carrying no account; see pages/Feedback.
        page = <Feedback branchCode={branchCode} unitCode={unitCode} />
      } else {
        page = <Display branchCode={branchCode} unitCode={unitCode} />
      }
      return <Suspense fallback={null}>{page}</Suspense>
    }

    if (!identity) {
      // Resolved here, at render, rather than where notice was set: this reruns
      // every time lang changes, so a notice shown before a language switch
      // still reads correctly after one.
      return <SignIn onSignedIn={signedIn} notice={noticeText(notice, lang)} onLanguage={onSignInLanguage} />
    }

    const staff = identity.staff

    // One wall: a password somebody else chose is a password somebody else knows.
    if (identity.mustChangePassword) {
      return (
        <ChangePassword staff={staff} forced onDone={() => refresh().catch(() => signOut())} />
      )
    }

    // The installation owner opens an organisation's page only once one is chosen.
    if (org.needed && pageNeedsOrganisation(path)) {
      const title = t(PAGE_TITLES[path.split('/')[1] ?? ''] ?? 'entry.app.reports', lang)
      return (
        <Chrome title={title}>
          <ChooseOrganisation title={title} />
        </Chrome>
      )
    }

    // Typed or bookmarked by a role without the tile: say so, not a page of refusals.
    // Bookings shut by the plan or licence say that; held back by a lapse, the page opens.
    const bookings = path === '/appointments' ? bookingsAccess(identity) : 'role'
    if (!identity.owner && !bookingsHeld(bookings) && !pageAllowed(path, tilesFor(identity))) {
      const title = t(PAGE_TITLES[path.split('/')[1] ?? ''] ?? 'entry.app.reports', lang)
      const shut = bookingsShutLine(bookings)
      return (
        <Chrome title={title}>
          <main className="page">
            <header className="page__head">
              <h1 className="page__title">{title}</h1>
            </header>
            {shut ? (
              <EmptyState icon={<Icon name="lock" size={20} />} title={t(shut, lang)} />
            ) : (
              <p className="empty">{t('common.noAccessPage', lang)}</p>
            )}
          </main>
        </Chrome>
      )
    }

    // Reports has addresses of its own: the list, /reports/<report>, /reports/tickets.
    if (path === '/reports' || path.startsWith('/reports/')) return <Reports identity={identity} onNavigate={navigate} path={path} />

    switch (path) {
      case '/reception':
        return <Reception identity={identity} onNavigate={navigate} onSignOut={signOut} />
      case '/counter':
        return <Counter identity={identity} onNavigate={navigate} onSignOut={signOut} />
      case '/floor':
        return <Floor identity={identity} onNavigate={navigate} onSignOut={signOut} />
      case '/appointments':
        return <Appointments identity={identity} onNavigate={navigate} onSignOut={signOut} />
      case '/people':
        return <People identity={identity} onNavigate={navigate} onChanged={refresh} />
      case '/administration':
        return <Administration identity={identity} onNavigate={navigate} />
      case '/screens':
        return <Layouts identity={identity} onNavigate={navigate} />
      case '/signage':
        return <Signage identity={identity} onNavigate={navigate} />
      case '/audit':
        return <Audit identity={identity} onNavigate={navigate} />
      case '/account':
        return <Account identity={identity} onNavigate={navigate} onSignOut={signOut} />
      case '/password':
        return (
          <ChangePassword
            staff={staff}
            forced={false}
            onDone={() => navigate('/')}
            onCancel={() => navigate('/')}
          />
        )
      default:
        // Every signed-in caller is a person, so there is one answer here.
        //
        // /kiosk and /display used to be addresses too — the same pages, taking
        // their branch from the signed-in account instead of from the URL — and a
        // machine holding an account was sent to one of them instead of the menu.
        // Those are gone. A machine is reached at /kiosk/BAKU-01/K1, it identifies
        // itself by that address and by nothing else, and there is no longer a
        // second way to be a kiosk.
        return <Home identity={identity} onNavigate={navigate} onSignOut={signOut} />
    }
  })()

  return (
    <StaffLanguageProvider language={lang} onChoose={ENGLISH_ONLY ? undefined : chooseLanguage}>
      <SessionProvider value={identity ? { identity, navigate: leave, signOut, refresh: reread, org } : null}>
        {/* A new organisation remounts the page, so everything it read is read again. */}
        <Fragment key={org.organisation}>{screen}</Fragment>
        <StaffToasts />
      </SessionProvider>
    </StaffLanguageProvider>
  )
}
